import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Sun, Moon, AlertCircle, Check } from "lucide-react";
import { useAuth, authHeaders, formatApiErrorDetail } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import {
  clearPendingInviteCode, postJoinPath, resolveInviteCode,
} from "../utils/postJoinRoute";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [Phase 2a.1] Fail-closed: anything other than literal "public" is admin-test.
const DEPLOY_MODE = process.env.REACT_APP_PGF_DEPLOY_MODE === "public" ? "public" : "admin_test";

function PasswordStrength({ password }) {
  const score = !password ? 0
    : [/[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/, /.{8,}/].filter(r => r.test(password)).length;
  const colors = ["", "bg-[#FF4444]", "bg-[#FF6B00]", "bg-yellow-400", "bg-[#00C853]"];
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  return (
    <div className="mt-1.5">
      <div className="flex gap-1 h-1">{[1,2,3,4].map(i => (
        <div key={i} className={`flex-1 rounded-full transition-all ${i <= score ? colors[score] : "bg-white/[0.10]"}`} />
      ))}</div>
      {password && <p className={`text-xs mt-1 font-body ${score >= 3 ? "text-[#00C853]" : score >= 2 ? "text-[#FF6B00]" : "text-[#FF4444]"}`}>{labels[score]}</p>}
    </div>
  );
}

export default function SignupPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const { search } = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // [P0 invite-gated access] Signup is invite-only while the deploy is
  // admin_test: an invited manager arrives with a code stashed by
  // JoinLeaguePage, and that code is what the backend validates. Without one,
  // a bookmark-navigation to /signup still bounces to /login.
  // Accepts BOTH entry points: /join/<code> (stashed) and /signup?code=<code>.
  // [P0 HOTFIX] Held in state, so it stays truthy for this page's lifetime.
  // It used to be re-read on every render: the moment the successful flow
  // called clearPendingInviteCode(), the re-render from `setLoading(false)`
  // saw no code and the invite-only guard below fired
  // `<Navigate to="/login" replace />` - bouncing a just-registered,
  // logged-in user to the login screen. That was the double-login.
  const [inviteCode] = useState(() => resolveInviteCode(search));
  if (DEPLOY_MODE === "admin_test" && !inviteCode) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    let registered = false;
    try {
      // Register returns a token, so the user is already logged in here.
      await register(form.first_name, form.last_name, form.email, form.password, inviteCode);
      registered = true;
      if (!inviteCode) { nav("/dashboard"); return; }
      // Consume the invite: join, then land in the lobby (or straight into a
      // draft that is already running).
      const { data } = await axios.post(
        `${API}/leagues/join-by-code/${inviteCode}`, {}, { headers: authHeaders() },
      );
      clearPendingInviteCode();
      let league = null;
      try {
        const res = await axios.get(`${API}/leagues/${data.league_id}`, { headers: authHeaders() });
        league = res.data;
      } catch { league = { id: data.league_id }; }
      nav(postJoinPath(league));
    } catch (err) {
      // [P0 HOTFIX] The account may already exist at this point (register
      // succeeded, the join or the league lookup did not). Never strand a
      // logged-in user on the signup form, and never send them to /login -
      // they are authenticated. Land them in the app.
      if (registered) {
        clearPendingInviteCode();
        nav("/dashboard");
        return;
      }
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create account. Please try again.");
    } finally { setLoading(false); }
  };

  const bg = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]";
  const inputCls = isDark
    ? "bg-[#0A0A0A] border-white/[0.10] focus:border-[#FF6B00] text-[#F5F5F5] placeholder-white/25"
    : "bg-white border-black/[0.10] focus:border-[#FF6B00] text-[#0A0A0A] placeholder-black/25";

  return (
    <div className={`min-h-screen ${bg} flex flex-col items-center justify-center px-4 py-10 relative`}>
      <button onClick={toggleTheme} className={`absolute top-4 right-4 w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${isDark ? "bg-white/[0.06] border-white/[0.10]" : "bg-black/[0.06] border-black/[0.08]"}`}>
        {isDark ? <Sun size={15} className="text-[#F5F5F5]" /> : <Moon size={15} className="text-[#0A0A0A]" />}
      </button>

      <Link to="/" className="flex items-center gap-3 mb-8">
        <div className="bg-[#FF6B00] px-2.5 py-1" style={{ boxShadow: isDark ? "0 0 16px rgba(255,107,0,0.40)" : "none" }}>
          <span className="font-display font-black text-white text-lg uppercase tracking-wider">PGF</span>
        </div>
        <span className={`font-display font-black text-xl uppercase tracking-widest ${text}`}>Pregame Fantasy</span>
      </Link>

      <div className="w-full max-w-[440px] glass-card rounded-2xl p-7">
        <h1 className={`font-display font-black text-3xl uppercase mb-1 ${text}`}>Create Account</h1>
        <p className={`font-body text-sm mb-6 ${muted}`}>Free forever. Upgrade for the edge.</p>

        {error && (
          <div className="bg-[#FF4444]/10 border border-[#FF4444]/40 rounded-xl px-4 py-3 flex items-center gap-2 mb-5" data-testid="signup-error">
            <AlertCircle size={15} className="text-[#FF4444] shrink-0" />
            <p className="text-[#FF4444] font-body text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[["first_name","First Name","Pat"], ["last_name","Last Name","Mahomes"]].map(([key, label, ph]) => (
              <div key={key}>
                <label className={`block font-body text-sm font-medium mb-1.5 ${muted}`}>{label}</label>
                <input type="text" data-testid={`signup-${key}`} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} placeholder={ph} required
                  className={`w-full border rounded-xl px-3 py-3 font-body text-sm outline-none transition-colors ${inputCls}`} />
              </div>
            ))}
          </div>
          <div>
            <label className={`block font-body text-sm font-medium mb-1.5 ${muted}`}>Email Address</label>
            <input type="email" data-testid="signup-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required
              className={`w-full border rounded-xl px-4 py-3 font-body text-sm outline-none transition-colors ${inputCls}`} />
          </div>
          <div>
            <label className={`block font-body text-sm font-medium mb-1.5 ${muted}`}>Password</label>
            <input type="password" data-testid="signup-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" required
              className={`w-full border rounded-xl px-4 py-3 font-body text-sm outline-none transition-colors ${inputCls}`} />
            <PasswordStrength password={form.password} />
          </div>
          <button type="submit" data-testid="signup-submit-btn" disabled={loading}
            className="w-full bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 text-white font-display font-black uppercase tracking-wide py-3 rounded-xl transition-all active:scale-[0.98] mt-2">
            {loading ? "Creating account..." : "Create Account - Free"}
          </button>
        </form>

        <p className={`text-center font-body text-xs mt-4 leading-relaxed ${muted}`}>
          By creating an account you agree to our{" "}
          <a href="/terms" className="text-[#FF6B00] hover:underline">Terms</a> and{" "}
          <a href="/privacy" className="text-[#FF6B00] hover:underline">Privacy Policy</a>.
        </p>
        <p className={`text-center font-body text-sm mt-4 ${muted}`}>
          Already have an account?{" "}<Link to="/login" className="text-[#FF6B00] hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
